import React, { ChangeEvent, useEffect, useState } from "react";

import { ButtonGroup, Form, ToggleButton } from "react-bootstrap";

import { ReferentialType } from "../../../_constants/referentialType";
import Drop from "../../../assets/svg/drop.svg";
import DropReverse from "../../../assets/svg/dropReverse.svg";
import Flake from "../../../assets/svg/flake.svg";
import Heater from "../../../assets/svg/heater.svg";
import SelectCreatable from "../../../common/components/SelectCreatable.tsx";
import { IReferential, IReferentials } from "../../referential/reducers";
import { DwellingEquipmentType, DwellingEquipmentTypeLabel } from "../enums";
import { IDwellingEquipment } from "../interfaces";

interface IProps {
  equipment: IDwellingEquipment;
  setEquipment: React.Dispatch<React.SetStateAction<IDwellingEquipment>>;
  installedAC?: boolean;
  setInstalledAC?: React.Dispatch<React.SetStateAction<boolean>>;
  referentials: IReferentials;
}

interface IEquipmentSpec {
  icon: string;
  label: string;
}

const DwellingEquipmentForm = ({
  equipment,
  setEquipment,
  installedAC,
  setInstalledAC,
  referentials,
}: IProps) => {
  const svgStyle = { width: "30px", height: "30px" };
  const [equipmentSpec, setEquipmentSpec] = useState<IEquipmentSpec>();

  useEffect(() => {
    switch (equipment.type) {
      case DwellingEquipmentType.AIR_CONDITIONER:
        setEquipmentSpec({
          icon: Flake,
          label:
            DwellingEquipmentTypeLabel[DwellingEquipmentType.AIR_CONDITIONER],
        });
        break;
      case DwellingEquipmentType.COLD_WATER:
        setEquipmentSpec({
          icon: DropReverse,
          label: DwellingEquipmentTypeLabel[DwellingEquipmentType.COLD_WATER],
        });
        break;
      case DwellingEquipmentType.HEATER:
        setEquipmentSpec({
          icon: Heater,
          label: DwellingEquipmentTypeLabel[DwellingEquipmentType.HEATER],
        });
        break;
      case DwellingEquipmentType.HOT_WATER:
        setEquipmentSpec({
          icon: Drop,
          label: DwellingEquipmentTypeLabel[DwellingEquipmentType.HOT_WATER],
        });
        break;
    }
  }, [equipment.type]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    handleClick(event.currentTarget.name, event.currentTarget.value);
  };
  const handleClick = (name: string, value: any) => {
    setEquipment({
      ...equipment,
      [name]: value,
    });
  };

  const [selectedEnergies, setSelectedEnergies] = useState<string[]>([]);

  const handleMultiEnergiesClick = (energyValue: any) => {
    setSelectedEnergies((prevSelectedEnergies) => {
      const updatedSelectedEnergies = prevSelectedEnergies.includes(energyValue)
        ? prevSelectedEnergies.filter((value) => value !== energyValue)
        : [...prevSelectedEnergies, energyValue];

      return updatedSelectedEnergies;
    });
  };

  useEffect(() => {
    const mappedEnergies: IReferential[] = selectedEnergies.map((value) => ({
      value,
      label: value,
    }));

    setEquipment((prevEquipment) => {
      const updatedEquipment: IDwellingEquipment = {
        ...prevEquipment,
        energies: mappedEnergies,
      };

      return updatedEquipment;
    });
  }, [selectedEnergies, setEquipment]);

  return (
    <div className="d-flex flex-column gap-3" style={{ minWidth: "270px" }}>
      <div className="d-flex">
        <div className="d-flex gap-3">
          <div className="svg-equipments d-flex justify-content-center align-items-center">
            <img
              src={equipmentSpec?.icon}
              alt={equipmentSpec?.label}
              style={svgStyle}
            />
          </div>
          <p className="m-0 w-75 d-flex align-items-center fw-bold p-equipments">
            {equipmentSpec?.label}
          </p>
        </div>
        {equipment.type === DwellingEquipmentType.AIR_CONDITIONER &&
          setInstalledAC !== undefined && (
            <div className="d-flex flex-wrap gap-2 align-items-center">
              <span className="ms-2 mb-0 add-prop-label">
                La climatisation est installée ?
              </span>
              <Form.Check
                type="switch"
                className="radio-order ms-2"
                checked={installedAC}
                onChange={() => setInstalledAC(!installedAC)}
              />
            </div>
          )}
      </div>
      {(equipment.type !== DwellingEquipmentType.AIR_CONDITIONER ||
        (equipment.type === DwellingEquipmentType.AIR_CONDITIONER &&
          installedAC)) && (
        <div className="d-flex flex-wrap flex-column flex-md-row gap-3 align-items-end">
          {equipment.type !== DwellingEquipmentType.COLD_WATER && (
            <>
              <div className="d-flex flex-column">
                <label className="add-prop-label ms-2">Système :</label>
                <ButtonGroup className="d-flex flex-row flex-nowrap">
                  <ToggleButton
                    id="radio-is-not-collective"
                    className="switch-dwelling-prop ms-1 switch-dwell"
                    checked={!equipment?.isCollective}
                    type="radio"
                    value="false"
                    onClick={() => handleClick("isCollective", false)}
                  >
                    Individuel
                  </ToggleButton>
                  <ToggleButton
                    id="radio-is-collective"
                    className="switch-dwelling-prop ms-1 switch-dwell"
                    checked={equipment?.isCollective}
                    type="radio"
                    value="true"
                    onClick={() => handleClick("isCollective", true)}
                  >
                    Collectif
                  </ToggleButton>
                </ButtonGroup>
              </div>
              {equipment.type !== DwellingEquipmentType.AIR_CONDITIONER && (
                <>
                  <div>
                    <label className="add-prop-label ms-2">Énergie(s) :</label>
                      <SelectCreatable
                        className={"select-creatable select-creatable-energy"}
                        options={referentials[ReferentialType.ENERGY].data}
                        getValue={(value) => handleClick("energies", value)}
                        isMulti={true}
                        creatable={true}
                        defaultValue={equipment.energies}
                      />
                  </div>
                </>
              )}
            </>
          )}
          {equipment.isCollective && (
            <>
              <div className="d-flex flex-column">
                <label className="add-prop-label ms-2">Compteur :</label>
                <input
                  style={{ minWidth: "250px" }}
                  className="add-prop-input"
                  type="text"
                  placeholder="Emplacement : logement, WC, gaine technique, escalier... "
                  name="location"
                  defaultValue={equipment.location}
                  onChange={handleChange}
                />
              </div>
              <div className="d-flex flex-column">
                <label className="add-prop-label ms-2">N° de compteur :</label>
                <input
                  style={{ maxWidth: "160px" }}
                  className="add-prop-input"
                  type="text"
                  name="number"
                  defaultValue={equipment.number}
                  onChange={handleChange}
                />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default DwellingEquipmentForm;
